// §24.12 (D-199, D-247) — a title's registration stub, and the dynamic import behind it.
//
// **This module is `@/gamekit`'s second door** (§24.1, M31, D-343), and the only one besides the
// barrel that `apps/**` may import. It exists because a registration stub needs exactly one
// function from the substrate and the barrel costs the whole substrate to get it.
//
// The door is safe only while this file stays what it is: React, `react-dom/client`, and **types**
// from `@hangar/shared`. It has no runtime dependency on `host.tsx`, `render.ts`, `loop.ts` or
// anything else in here, and it must not acquire one — a single value import from a sibling would
// put the substrate back in the boot path and nothing would look different. `boundaries.test.ts`
// asserts that, so the rule is checked rather than remembered.
//
// D-199's requirement is that **no game code sits in the shell's entry chunk**. That cannot be
// satisfied by the Store or the precache filter alone: `apps/index.ts` has to name a title to
// register it, and a plain `import` of the view pulls the whole game — engine, view, CSS — into
// the entry bundle regardless of whether anyone ever opens it.
//
// So a title's `index.tsx` declares only its *manifest* — id, name, glyph, gate, jump list — and
// hands over a thunk. The thunk's `import()` is what Vite splits on, and `vite.config.ts` names
// the resulting chunk `game.<name>-<hash>.js` so `isGameArtifact` can exclude it from the
// service-worker precache exactly.
//
// The split has to happen at the **view** boundary, not at the app object: `mount` is synchronous
// (§9.1) and the registry needs the manifest at `buildKernel()` time, long before anyone launches
// anything. React.lazy inside the mounted tree is what reconciles those two facts.
import { Suspense, lazy, type ComponentType } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { HangarAPI, InProcessApp } from '@hangar/shared';

/** What a lazily-loaded title's view is handed. Matches the eager apps' props exactly. */
export interface GameViewProps {
  api: HangarAPI;
  /** Injected in tests; the app id in production. */
  seed?: string;
  /**
   * §9.3 — subscribe to this window's jump-list commands; returns an unsubscribe.
   *
   * Delivered as a prop rather than a DOM event because the view renders *inside* the mounted
   * container and an event dispatched on that container would never reach it.
   */
  subscribeCommands?: (handler: (commandId: string) => void) => () => void;
}

export interface LazyAppSpec<P extends object = Record<never, never>> {
  /**
   * Everything the registry, launcher, bar and Store need before the chunk exists.
   *
   * `mount`/`unmount` are supplied here — declaring them would defeat the point, since a title
   * that could mount itself would have to import its own view.
   */
  app: Omit<InProcessApp, 'mount' | 'unmount'>;
  /**
   * The dynamic import. **Must be an `import()` expression written in the title's own
   * `index.tsx`** — a thunk built anywhere else moves the split point and quietly puts the game
   * back in the entry chunk.
   */
  load: () => Promise<{ default: ComponentType<GameViewProps & P> }>;
  /**
   * Extra props handed to the view on every mount — in practice a **D-059 capability**.
   *
   * `HangarAPI` is closed (§4.1), so a title that needs more than it grants — Cascade reads two
   * §14 numbers — receives it here, injected by `App.tsx` at composition. It never travels through
   * `mount`, so an external app can never obtain one.
   */
  props?: P;
}

/**
 * Build an `InProcessApp` whose code arrives on first launch.
 *
 * `lazy()` is called **once per app**, not per mount: React caches the resolved module on the
 * component it returns, so a second window (or a re-open of the same one) reuses the already
 * downloaded chunk instead of re-entering the import.
 */
export function createLazyApp<P extends object = Record<never, never>>(
  spec: LazyAppSpec<P>,
): InProcessApp {
  const View = lazy(spec.load);
  // One root per window (D-057), the same contract every eager built-in keeps.
  const roots = new WeakMap<HTMLElement, Root>();
  /**
   * Per-window command subscribers, keyed by the **api instance**.
   *
   * `mount` is handed `(container, api)` and `onCommand` is handed `(commandId, api, context)`, so
   * the api object is the only identity the two calls share — and the mount service builds exactly
   * one per window (§9.1), which is precisely the scope a jump-list entry should reach.
   */
  const subscribers = new WeakMap<HangarAPI, Set<(commandId: string) => void>>();

  return {
    ...spec.app,

    mount(container, api) {
      const root = createRoot(container);
      roots.set(container, root);
      const handlers = new Set<(commandId: string) => void>();
      subscribers.set(api, handlers);

      const subscribeCommands = (handler: (commandId: string) => void): (() => void) => {
        handlers.add(handler);
        return () => handlers.delete(handler);
      };

      root.render(
        // The fallback is deliberately plain and unstyled-by-token: it exists for the few hundred
        // milliseconds of a cold chunk fetch, and anything more elaborate would flash on the warm
        // path where the module is already in memory.
        <Suspense fallback={<div className="gk-loading" data-testid="gk-loading" />}>
          <View api={api} subscribeCommands={subscribeCommands} {...(spec.props ?? ({} as P))} />
        </Suspense>,
      );
    },

    unmount(container) {
      const root = roots.get(container);
      roots.delete(container);
      // React refuses a synchronous unmount from inside its own commit; the host layer calls this
      // from a store subscription, which is outside one.
      root?.unmount();
    },

    // §9.3 — a jump-list entry chosen from the taskbar. A command that arrives before the lazy
    // chunk has resolved simply finds no subscribers and is dropped, which is the right answer:
    // there is no game yet to start a new round of.
    onCommand(commandId, api) {
      for (const handler of subscribers.get(api) ?? []) handler(commandId);
    },
  };
}
